import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

interface Device {
  id: string;
  name: string;
  ip: string;
  location?: string;
  status: 'online' | 'offline';
}

interface DeviceEditDialogProps {
  open: boolean;
  device: Device | null;
  onClose: () => void;
  onSave: (deviceId: string, updates: Partial<Device>) => Promise<void>;
}

const DeviceEditDialog: React.FC<DeviceEditDialogProps> = ({
  open,
  device,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    ip: '',
    location: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 当设备变化时更新表单数据
  useEffect(() => {
    if (device) {
      setFormData({
        name: device.name || '',
        ip: device.ip || '',
        location: device.location || '',
      });
      setError(null);
      setSuccess(null);
    }
  }, [device]);

  const handleInputChange = (field: keyof typeof formData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('设备名称不能为空');
      return false;
    }
    if (!formData.ip.trim()) {
      setError('IP地址不能为空');
      return false;
    }
    
    // 验证IP地址格式
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    if (!ipRegex.test(formData.ip)) {
      setError('IP地址格式不正确');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!device || !validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await onSave(device.id, {
        name: formData.name.trim(),
        ip: formData.ip.trim(),
        location: formData.location.trim(),
      });
      
      setSuccess('设备信息更新成功！');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        编辑设备信息
        {device && (
          <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>
            (ID: {device.id})
          </span>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="设备名称"
              value={formData.name}
              onChange={handleInputChange('name')}
              disabled={loading}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="IP地址"
              value={formData.ip}
              onChange={handleInputChange('ip')}
              disabled={loading}
              required
              placeholder="例如: 192.168.1.100"
              helperText="请输入设备的IP地址"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="位置"
              value={formData.location}
              onChange={handleInputChange('location')}
              disabled={loading}
              placeholder="例如: 美国办公室"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          取消
        </Button>
        <Button
          onClick={handleSave}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeviceEditDialog;
